import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Box, TextField, IconButton, Accordion, AccordionSummary, AccordionDetails, Typography, Tooltip } from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Delete as DeleteIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  DragIndicator as DragIndicatorIcon,
} from '@mui/icons-material';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import { SectionFieldCollection, SectionFieldDTO, FieldEntryDTO, FieldType, DynamicTableColumnDef } from '../magic-report.type';
import { BookmarkToggle } from './BookmarkToggle';
import { CommentsSection } from './CommentsSection';
import { getCommentsField } from './section-utils';
import { usePrompt } from '../../../AppPrompt';
import { LastMonthFieldToggle } from './LastMonthFieldToggle';
import { useMagicReport } from '../context/MagicReportContext';
import { getNextActionItemOrder, getSectionRank } from './action-items.util';
import { ActionItemsReorderModal } from './ActionItemsReorderModal';
import { DynamicSectionTable } from './DynamicSectionTable';

const DYNAMIC_TABLE_COLUMNS_FIELD = 'dynamicTableColumns';
const DYNAMIC_TABLE_ROWS_FIELD = 'dynamicTableRows';

function parseTableColumns(value: string | undefined): DynamicTableColumnDef[] | null {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) && parsed.length > 0 && parsed.every((c: any) => c?.id && c?.header != null) ? parsed : null;
  } catch {
    return null;
  }
}

interface DynamicSectionItemProps {
  sectionData: SectionFieldCollection;
  sectionIndex: number;
  onDelete: () => void;
  onUpdate: (updatedData: SectionFieldCollection) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
}

/**
 * DynamicSectionItem - Renders a single user-created dynamic section
 * Uses a simple accordion with inline editing for header and body
 */
export const DynamicSectionItem: React.FC<DynamicSectionItemProps> = ({
  // magicReportId is intentionally unused here; header + body are fully local to this section
  sectionData,
  sectionIndex,
  onDelete,
  onUpdate,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
}) => {
  const { getConfirmation } = usePrompt();
  const [expanded, setExpanded] = useState(true);
  const [addTableDialogOpen, setAddTableDialogOpen] = useState(false);
  const [reorderModalOpen, setReorderModalOpen] = useState(false);

  const { reportState, registerSectionFlush, unregisterSectionFlush, registerSectionOrderSync, unregisterSectionOrderSync } =
    useMagicReport() as any;
  // Registry key must match the `dynamicSections[idx]` form used by getBookmarkedSections/reorderSectionActionItems
  const sectionRegistryKey = `dynamicSections[${sectionIndex}]`;
  const sectionActionItemOrderRef = React.useRef<number | undefined>(undefined);
  const onBook = React.useCallback((): number => {
    if (sectionActionItemOrderRef.current !== undefined) return sectionActionItemOrderRef.current;
    const order = getNextActionItemOrder(reportState.sections);
    sectionActionItemOrderRef.current = order;
    return order;
  }, [reportState.sections]);

  // Live rank derived from global state — recomputes whenever any section is bookmarked/unbookmarked,
  // same as MagicReportSection so dynamic sections show the same "#N" reorder chip.
  const sectionRank = useMemo(() => getSectionRank(reportState.sections, sectionRegistryKey), [reportState.sections, sectionRegistryKey]);
  const headerField = sectionData.fields.find(f => f.fieldName === 'header');
  const bodyField = sectionData.fields.find(f => f.fieldName === 'body');
  const commentsField = getCommentsField(sectionData);
  const tableColumnsField = sectionData.fields.find(f => f.fieldName === DYNAMIC_TABLE_COLUMNS_FIELD);
  const tableRowsField = sectionData.fields.find(f => f.fieldName === DYNAMIC_TABLE_ROWS_FIELD);
  const tableColumns = useMemo(() => parseTableColumns(tableColumnsField?.value), [tableColumnsField?.value]);
  const hasTable = Boolean(tableColumns && tableColumns.length > 0);

  const headerValue = headerField?.value || '';
  const bodyValue = bodyField?.value || '';
  const bodyBookmarked = !!bodyField?.addToActionItems;

  // Local state to avoid updating global state on every keystroke
  const [localBodyValue, setLocalBodyValue] = useState<string>(bodyValue as string);
  const [localCommentsField, setLocalCommentsField] = useState<SectionFieldDTO | undefined>(commentsField);

  // Keep local values in sync if the underlying field changes externally
  useEffect(() => {
    setLocalBodyValue(bodyValue as string);
  }, [bodyValue]);

  useEffect(() => {
    setLocalCommentsField(commentsField);
  }, [commentsField]);

  // Check if all bookmarks in the section are checked
  const allBookmarksChecked = useMemo(() => {
    if (!sectionData?.fields || sectionData.fields.length === 0) return false;

    // Collect all bookmarkable items (fields and entries)
    const bookmarkableFields: SectionFieldDTO[] = [];
    const bookmarkableEntries: Array<{ field: SectionFieldDTO; entry: FieldEntryDTO }> = [];

    sectionData.fields.forEach(f => {
      if (f.addToActionItems !== undefined) {
        bookmarkableFields.push(f);
      }
      if (f.entries && f.entries.length > 0) {
        f.entries.forEach(e => {
          // Treat all entries as bookmarkable, even if addToActionItems is currently undefined
          bookmarkableEntries.push({ field: f, entry: e });
        });
      }
    });

    if (bookmarkableFields.length === 0 && bookmarkableEntries.length === 0) return false;

    const allFieldsChecked = bookmarkableFields.every(f => f.addToActionItems === true);
    const allEntriesChecked = bookmarkableEntries.every(({ entry }) => entry.addToActionItems === true);

    return allFieldsChecked && allEntriesChecked;
  }, [sectionData?.fields]);

  // Handler to toggle all bookmarks in the dynamic section
  const handleToggleAllBookmarks = useCallback(
    (e?: React.MouseEvent) => {
      // Stop event propagation to prevent accordion from toggling
      if (e) {
        e.stopPropagation();
      }

      if (!sectionData?.fields) return;

      const newValue = !allBookmarksChecked;
      if (!newValue) {
        sectionActionItemOrderRef.current = undefined;
      }
      const nextOrder = newValue ? onBook() : 0;

      const updatedFields = sectionData.fields.map((f: SectionFieldDTO) => {
        // Always process fields that have entries (e.g. Comments) so global bookmark toggles comment entries
        if (f.entries && f.entries.length > 0) {
          const updatedEntries = f.entries.map((e: FieldEntryDTO) => ({
            ...e,
            addToActionItems: newValue,
            actionItemOrder: newValue ? (e.actionItemOrder ?? nextOrder) : undefined,
          }));
          return {
            ...f,
            entries: updatedEntries,
            ...(f.addToActionItems !== undefined && {
              addToActionItems: newValue,
              actionItemOrder: newValue ? (f.actionItemOrder ?? nextOrder) : undefined,
            }),
          };
        }
        // Field-level bookmark (e.g. body)
        if (f.addToActionItems === undefined) return f;
        return {
          ...f,
          addToActionItems: newValue,
          actionItemOrder: newValue ? (f.actionItemOrder ?? nextOrder) : undefined,
        };
      });

      onUpdate({ fields: updatedFields });
    },
    [sectionData?.fields, allBookmarksChecked, onUpdate, onBook],
  );

  // Handle body change
  const handleBodyChange = useCallback((newBody: string) => {
    setLocalBodyValue(newBody);
  }, []);

  // Debounce syncing body changes back to parent/global state
  useEffect(() => {
    const handler = setTimeout(() => {
      // Avoid unnecessary updates if value hasn't actually changed
      if (localBodyValue === (bodyField?.value || '')) {
        return;
      }

      const updatedFields = sectionData.fields.map(f =>
        f.fieldName === 'body'
          ? {
              ...f,
              value: localBodyValue,
            }
          : f,
      );

      onUpdate({ fields: updatedFields });
    }, 300);

    return () => clearTimeout(handler);
  }, [localBodyValue, bodyField?.value, sectionData.fields, onUpdate]);

  // Handle comments change
  const handleCommentsChange = useCallback((updatedField: SectionFieldDTO) => {
    setLocalCommentsField(updatedField);
  }, []);

  // Debounce syncing comments changes back to parent/global state
  useEffect(() => {
    if (!localCommentsField) return;

    const handler = setTimeout(() => {
      // Avoid unnecessary updates if comments haven't changed from external state
      const currentCommentsField = sectionData.fields.find(f => f.fieldName === 'Comments');
      if (JSON.stringify(localCommentsField) === JSON.stringify(currentCommentsField)) {
        return;
      }

      const updatedFields = sectionData.fields.map(f =>
        f.fieldName === 'Comments'
          ? {
              ...f,
              ...localCommentsField,
            }
          : f,
      );

      onUpdate({ fields: updatedFields });
    }, 300);

    return () => clearTimeout(handler);
  }, [localCommentsField, sectionData.fields, onUpdate]);

  // Immediately push any pending local edits (body/comments) to global state, bypassing the debounce.
  // Exposed to the shared sync manager so a reorder-commit can flush this section before it reorders.
  const flushPendingUpdates = useCallback(() => {
    let updatedFields = sectionData.fields;
    if (localBodyValue !== (bodyField?.value || '')) {
      updatedFields = updatedFields.map(f => (f.fieldName === 'body' ? { ...f, value: localBodyValue } : f));
    }
    const currentCommentsField = sectionData.fields.find(f => f.fieldName === 'Comments');
    if (localCommentsField && JSON.stringify(localCommentsField) !== JSON.stringify(currentCommentsField)) {
      updatedFields = updatedFields.map(f => (f.fieldName === 'Comments' ? { ...f, ...localCommentsField } : f));
    }
    if (updatedFields !== sectionData.fields) {
      onUpdate({ fields: updatedFields });
    }
  }, [sectionData.fields, localBodyValue, bodyField?.value, localCommentsField, onUpdate]);

  // Register flush function with the shared sync manager on mount, unregister on unmount
  useEffect(() => {
    if (registerSectionFlush) {
      registerSectionFlush(sectionRegistryKey, flushPendingUpdates);
    }
    return () => {
      if (unregisterSectionFlush) {
        unregisterSectionFlush(sectionRegistryKey);
      }
    };
  }, [sectionRegistryKey, flushPendingUpdates, registerSectionFlush, unregisterSectionFlush]);

  // Register order-sync callback so an action-items reorder patches this section's local field
  // references (actionItemOrder) without waiting on a full re-render from global state.
  // Only Comments needs patching here: the body field's actionItemOrder lives on sectionData/props
  // (not local state), so it's already kept current via the parent's onUpdate → sectionData flow.
  useEffect(() => {
    if (!registerSectionOrderSync) return;
    registerSectionOrderSync(sectionRegistryKey, (updatedSections: any) => {
      const updatedCollection = updatedSections?.dynamicSections?.[sectionIndex];
      const updatedCommentsField = updatedCollection?.fields?.find((f: SectionFieldDTO) => f.fieldName === 'Comments');
      if (!updatedCommentsField) return;

      setLocalCommentsField(prev => {
        if (!prev) return prev;
        const updatedEntries = (prev.entries || []).map((e: FieldEntryDTO, ei: number) => {
          const me = updatedCommentsField.entries?.[ei];
          return me !== undefined ? { ...e, actionItemOrder: me.actionItemOrder } : e;
        });
        return { ...prev, actionItemOrder: updatedCommentsField.actionItemOrder, entries: updatedEntries };
      });
    });
    return () => {
      if (unregisterSectionOrderSync) unregisterSectionOrderSync(sectionRegistryKey);
    };
  }, [sectionRegistryKey, sectionIndex, registerSectionOrderSync, unregisterSectionOrderSync]);

  const handleAddTableConfirm = useCallback(
    (columns: DynamicTableColumnDef[]) => {
      const emptyRow = {
        _id: `row-${Date.now()}`,
        ...columns.reduce<Record<string, string | number>>((acc, col) => {
          if (col.type === 'numeric' || col.type === 'currency') {
            acc[col.id] = 0;
          } else {
            acc[col.id] = '';
          }
          return acc;
        }, {}),
      };

      const newColumnsField: SectionFieldDTO = {
        fieldName: DYNAMIC_TABLE_COLUMNS_FIELD,
        fieldType: FieldType.Text,
        value: JSON.stringify(columns),
      };
      const newRowsField: SectionFieldDTO = {
        fieldName: DYNAMIC_TABLE_ROWS_FIELD,
        fieldType: FieldType.TextArray,
        entries: [
          {
            order: 0,
            value: JSON.stringify(emptyRow),
          },
        ],
      };
      onUpdate({
        fields: [...sectionData.fields, newColumnsField, newRowsField],
      });
      setAddTableDialogOpen(false);
    },
    [sectionData.fields, onUpdate],
  );

  const handleRemoveTable = useCallback(async () => {
    const isConfirmed = await getConfirmation({
      heading: 'Remove table',
      description: 'Are you sure you want to remove this table? All row data will be lost.',
      confirmButton: { label: 'Remove', color: 'error' },
      cancelButton: { label: 'Cancel', color: 'secondary' },
    });
    if (!isConfirmed) return;
    onUpdate({
      fields: sectionData.fields.filter(f => f.fieldName !== DYNAMIC_TABLE_COLUMNS_FIELD && f.fieldName !== DYNAMIC_TABLE_ROWS_FIELD),
    });
  }, [sectionData.fields, onUpdate, getConfirmation]);

  const handleTableFieldChange = useCallback(
    (fieldName: string, updates: Partial<SectionFieldDTO>) => {
      const updatedFields = sectionData.fields.map(f => (f.fieldName === fieldName ? { ...f, ...updates } : f));
      onUpdate({ fields: updatedFields });
    },
    [sectionData.fields, onUpdate],
  );

  const handleDelete = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      const isConfirmed = await getConfirmation({
        heading: 'Delete Section',
        description: `Are you sure you want to delete the section "${headerValue || 'this section'}"? This action cannot be undone.`,
        confirmButton: { label: 'Delete', color: 'error' },
        cancelButton: { label: 'Cancel', color: 'secondary' },
      });
      if (isConfirmed) {
        onDelete();
      }
    },
    [onDelete, getConfirmation, headerValue],
  );

  const handleMoveUp = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onMoveUp();
    },
    [onMoveUp],
  );

  const handleMoveDown = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onMoveDown();
    },
    [onMoveDown],
  );

  return (
    <Box id={`dynamic-section-${sectionIndex}`} sx={{ mb: 4 }}>
      <Accordion expanded={expanded} onChange={() => setExpanded(!expanded)} sx={{ px: 2, borderRadius: '10px !important' }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ p: 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', pr: 2 }}>
            <Typography variant='h5' color='primary' sx={{ lineHeight: 1.5 }}>
              {headerValue || 'New Section'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {sectionRank !== undefined ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    borderRadius: '999px',
                    bgcolor: 'grey.200',
                    overflow: 'hidden',
                  }}
                >
                  <Tooltip title='Reorder action items' placement='top'>
                    <Box
                      onClick={e => {
                        e.stopPropagation();
                        setReorderModalOpen(true);
                      }}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        pl: 1.5,
                        pr: 1.25,
                        py: 0.625,
                        cursor: 'pointer',
                        userSelect: 'none',
                        '&:hover': { bgcolor: 'grey.300' },
                        transition: 'background-color 0.15s',
                      }}
                    >
                      <DragIndicatorIcon sx={{ fontSize: '1.1rem', color: 'text.secondary' }} />
                      <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: 'text.primary', lineHeight: 1 }}>
                        {sectionRank}
                      </Typography>
                    </Box>
                  </Tooltip>
                  <Box sx={{ width: '1px', height: 20, bgcolor: 'grey.400', flexShrink: 0 }} />
                  <Tooltip title={allBookmarksChecked ? 'Remove all bookmarks' : 'Bookmark all'} placement='top'>
                    <Box
                      onClick={e => {
                        e.stopPropagation();
                        handleToggleAllBookmarks(e);
                      }}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        pl: 1.25,
                        pr: 1.5,
                        py: 0.625,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'grey.300' },
                        transition: 'background-color 0.15s',
                      }}
                    >
                      {allBookmarksChecked ? (
                        <BookmarkIcon sx={{ fontSize: '1.2rem', color: 'primary.main' }} />
                      ) : (
                        <BookmarkBorderIcon sx={{ fontSize: '1.2rem', color: 'text.disabled' }} />
                      )}
                    </Box>
                  </Tooltip>
                </Box>
              ) : (
                <Box onClick={handleToggleAllBookmarks} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <BookmarkToggle
                    addToActionItems={allBookmarksChecked}
                    onToggle={handleToggleAllBookmarks}
                    sx={{ fontSize: '1.5rem', display: 'flex', alignItems: 'center' }}
                  />
                </Box>
              )}
              <Tooltip title='Move section up' placement='top'>
                <span>
                  <IconButton
                    size='small'
                    onClick={handleMoveUp}
                    disabled={!canMoveUp}
                    sx={{ color: canMoveUp ? 'text.secondary' : 'action.disabled' }}
                  >
                    <ArrowUpIcon fontSize='small' />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title='Move section down' placement='top'>
                <span>
                  <IconButton
                    size='small'
                    onClick={handleMoveDown}
                    disabled={!canMoveDown}
                    sx={{ color: canMoveDown ? 'text.secondary' : 'action.disabled' }}
                  >
                    <ArrowDownIcon fontSize='small' />
                  </IconButton>
                </span>
              </Tooltip>
              <IconButton size='small' onClick={handleDelete} sx={{ color: 'error.main' }}>
                <DeleteIcon fontSize='small' />
              </IconButton>
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0, pb: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            {/* Body Field with bookmark (action item toggle) */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {/* LastMonth toggle and content */}
              <LastMonthFieldToggle lastMonthValue={bodyField?.lastMonth} label='Section Body' />

              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <TextField
                  value={localBodyValue}
                  onChange={e => handleBodyChange(e.target.value)}
                  fullWidth
                  multiline
                  rows={6}
                  variant='outlined'
                  size='small'
                  placeholder='Enter section content...'
                />

                <BookmarkToggle
                  addToActionItems={bodyBookmarked}
                  onToggle={() => {
                    const newValue = !bodyBookmarked;
                    const nextOrder = onBook();
                    onUpdate({
                      fields: sectionData.fields.map(f =>
                        f.fieldName === 'body'
                          ? {
                              ...f,
                              addToActionItems: newValue,
                              actionItemOrder: newValue ? (f.actionItemOrder ?? nextOrder) : undefined,
                            }
                          : f,
                      ),
                    });
                  }}
                  sx={{ mt: 1 }}
                />
              </Box>
            </Box>

            {/* Optional table */}
            {hasTable && tableColumns && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <DynamicSectionTable columns={tableColumns} rowsField={tableRowsField} handleFieldChange={handleTableFieldChange} />
              </Box>
            )}

            {/* Comments Section */}
            {localCommentsField && (
              <CommentsSection commentsField={localCommentsField} onChange={handleCommentsChange} title='Comments' onBook={onBook} />
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
      <ActionItemsReorderModal open={reorderModalOpen} onClose={() => setReorderModalOpen(false)} />
    </Box>
  );
};
